import { readFileSync } from 'fs';

import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import LayoutRouter from '../../components/layout/LayoutRouter';
import AnonymousMarketplaceHero from '../../components/marketing/AnonymousMarketplaceHero';
import { MarketplaceContainer } from '../../components/marketplace';
import { ServiceGridSSR } from '../../components/marketplace/ServiceGridSSR';
import type { RootState } from '../../store';

// =============================================================================
// SSR HELPER FUNCTIONS - Internal API URL resolution
// =============================================================================

/**
 * Resolve the correct API port for SSR requests.
 *
 * Priority:
 * 1. API_PORT env var (set per-environment in PM2 ecosystem config)
 *    - Dev: API_PORT=3010
 *    - Prod blue: API_PORT=3020
 *    - Prod green: API_PORT=3030
 * 2. .active_env file (legacy production blue/green detection)
 * 3. Fallback: 3020 (production blue default)
 */
function getApiPort(): string {
  // Prefer explicit API_PORT from PM2 ecosystem config
  if (process.env.API_PORT) {
    console.log('[SSR-CATALOG] Using API_PORT from env:', process.env.API_PORT);
    return process.env.API_PORT;
  }

  // Read .active_env for environment detection (dev/blue/green)
  const homePath = process.env.HOME || '/home/aithonone';
  const activeEnvFile = `${homePath}/public_html/.active_env`;
  try {
    const result = readFileSync(activeEnvFile, 'utf8').trim();
    console.log('[SSR-CATALOG] Read .active_env:', { path: activeEnvFile, result });
    // Dev environment uses port 3010, prod blue=3020, prod green=3030
    if (result === 'dev') return '3010';
    if (result === 'green') return '3030';
    return '3020'; // blue (default for production)
  } catch (error) {
    console.log('[SSR-CATALOG] Could not read .active_env, using default port 3020');
    return '3020';
  }
}

function getInternalApiUrl(path: string): string {
  const port = getApiPort();
  const url = `http://127.0.0.1:${port}${path}`;
  console.log('[SSR-CATALOG] Internal API URL:', { path, port, url });
  return url;
}

// =============================================================================

interface CatalogData {
  catalog: {
    id: string;
    name: string;
    description?: string;
    branding: any;
    allow_perks?: boolean;
    is_main_catalog?: boolean;
  };
  services: any[];
}

const truncateStr = (text: string, max: number) =>
  text.length <= max ? text : text.substring(0, max - 3) + '...';

// Catalog instance page - uses SSR-cached catalog data
const CatalogInstancePage: React.FC<{ catalogInstance: string; catalogData?: CatalogData }> = ({
  catalogInstance,
  catalogData: initialCatalogData,
}) => {
  const [catalogData, setCatalogData] = useState<CatalogData | null>(initialCatalogData || null);
  const [hydrated, setHydrated] = React.useState(false);
  React.useEffect(() => { setHydrated(true); }, []);
  const [loading, setLoading] = useState(!initialCatalogData);
  const [error, setError] = useState<string | null>(null);

  const user = useSelector((state: RootState) => state.auth.user);
  const token = useSelector((state: RootState) => state.auth.token);
  const showAnonymousHero = (!hydrated || !user) && initialCatalogData?.catalog?.is_main_catalog !== false;

  // Only fetch if SSR data wasn't available
  useEffect(() => {
    if (initialCatalogData) {
      // SSR data already available, no need to fetch
      setCatalogData(initialCatalogData);
      setLoading(false);
      return;
    }

    const fetchCatalog = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/catalog-context/${catalogInstance}`);
        if (!response.ok) {
          throw new Error(`Catalog fetch failed: ${response.status}`);
        }
        const data = await response.json();
        // Strip perk data from services when catalog has allow_perks = false
        if (data?.catalog?.allow_perks === false && data?.items) {
          data.items = data.items.map((s: any) => ({ ...s, winningPerk: null, hasWinningPerk: false }));
        }
        setCatalogData({
          catalog: data?.catalog || null,
          services: data?.items || [],
        });
      } catch {
        setError('Failed to load catalog');
      } finally {
        setLoading(false);
      }
    };

    fetchCatalog();
  }, [catalogInstance, initialCatalogData]);

  // Track catalog visit for authenticated users
  useEffect(() => {
    if (user?.id && catalogInstance && token) {
      fetch('/api/user/track-catalog-visit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ catalogInstance }),
      }).catch(() => {
        // Silently fail catalog visit tracking
      });
    }
  }, [user?.id, catalogInstance, token]);

  if (loading && !initialCatalogData) {
    return (

      <LayoutRouter>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </LayoutRouter>
    );
  }

  if ((error || !catalogData) && !initialCatalogData) {
    return (

      <LayoutRouter>
        <Alert severity="error">Catalog "{catalogInstance}" not found or unavailable.</Alert>
      </LayoutRouter>
    );
  }

  // Even if loading, if we have SSR data, use it for branding
  const effectiveCatalogData = catalogData || initialCatalogData;
  if (!effectiveCatalogData) {
    return (
      <LayoutRouter>
        <Alert severity="error">Catalog "{catalogInstance}" not found or unavailable.</Alert>
      </LayoutRouter>
    );
  }

  // Use the actual route slug as the canonical catalog id for client-side
  // catalog/service loading and URL generation. Deriving a slug from the
  // display name can drift from the real public catalog slug and cause the
  // hydrated marketplace container to fetch the wrong catalog context.
  const catalogSlug = catalogInstance;

  return (
    <>
      <Head>
        <title>{effectiveCatalogData.catalog.name} - Aithon Marketplace</title>
        <meta
          name="description"
          content={
            effectiveCatalogData.catalog.description ||
            `Professional services from ${effectiveCatalogData.catalog.name}`
          }
        />
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href={`https://aithon.tech/c/${catalogInstance}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${effectiveCatalogData.catalog.name} — Aithon`} />
        <meta property="og:description" content={truncateStr(effectiveCatalogData.catalog.description || `Browse professional services from ${effectiveCatalogData.catalog.name} on Aithon`, 200)} />
        <meta property="og:url" content={`https://aithon.tech/c/${catalogInstance}`} />
        <meta property="og:image" content={'https://aithon.tech/og-default.png'} />
        <meta property="og:site_name" content="Aithon" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${effectiveCatalogData.catalog.name} — Aithon`} />
        <meta name="twitter:description" content={truncateStr(effectiveCatalogData.catalog.description || `Professional services from ${effectiveCatalogData.catalog.name}`, 200)} />
        <meta name="twitter:image" content={'https://aithon.tech/og-default.png'} />

        {/* JSON-LD: ItemList for all services in this catalog */}
        {effectiveCatalogData.services && effectiveCatalogData.services.length > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'ItemList',
                name: `${effectiveCatalogData.catalog.name} Services`,
                description:
                  effectiveCatalogData.catalog.description ||
                  `Professional services from ${effectiveCatalogData.catalog.name}`,
                numberOfItems: effectiveCatalogData.services.length,
                itemListElement: effectiveCatalogData.services.slice(0, 50).map((svc: any, idx: number) => ({
                  '@type': 'ListItem',
                  position: idx + 1,
                  item: {
                    '@type': 'Service',
                    name: svc.name || svc.service_name,
                    description: svc.description || svc.short_description || '',
                    url: `https://aithon.tech/c/${catalogInstance}/services/${svc.id || svc.service_id}`,
                    ...(svc.base_price > 0
                      ? {
                          offers: {
                            '@type': 'Offer',
                            price: (svc.base_price / 100).toFixed(2),
                            priceCurrency: 'USD',
                            availability: 'https://schema.org/InStock',
                          },
                        }
                      : {}),
                    ...(svc.brand_name
                      ? { brand: { '@type': 'Brand', name: svc.brand_name } }
                      : {}),
                    ...(svc.category ? { category: svc.category } : {}),
                  },
                })),
              }),
            }}
          />
        )}

        {/* JSON-LD: BreadcrumbList */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Aithon Marketplace',
                  item: 'https://aithon.tech/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: effectiveCatalogData.catalog.name,
                  item: `https://aithon.tech/c/${catalogInstance}`,
                },
              ],
            }),
          }}
        />
      </Head>

      


      {showAnonymousHero && (
        <AnonymousMarketplaceHero
          eyebrow="BETA · REAL PRICING · TRANSPARENT COMPETITION"
          title="Compare business services. Real pricing. No sales calls."
          description="When pricing is visible and providers compete in the open, the best offer wins — not the best salesperson. Stop calling three vendors for the same quote."
          browseHref="#catalog"
          accentColor={effectiveCatalogData.catalog.branding?.primaryColor || '#c9a227'}
        />
      )}

      {/* Category nav — SSR-rendered, crawlable. CSS hides instantly on JS load; !hydrated removes from DOM after React mount */}
      {!hydrated && effectiveCatalogData.catalog.is_main_catalog !== false && effectiveCatalogData.services && effectiveCatalogData.services.length > 0 && (
        <Box id="catalog" sx={{ bgcolor: "background.paper", borderBottom: 1, borderColor: "divider", py: 1.5, px: 2 }}>
          <Box sx={{ maxWidth: 1200, mx: "auto", display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 1, mr: 1 }}>Browse:</Typography>
            {(Array.from(new Set((effectiveCatalogData.services as any[]).map((s: any) => s.category).filter(Boolean))) as string[]).map((cat: string) => (
              <Box key={cat} component="a" href={"/c/" + catalogInstance + "?category=" + encodeURIComponent(cat)}
                sx={{ fontSize: "0.75rem", color: "text.secondary", textDecoration: "none", px: 1, py: 0.25, borderRadius: 1, border: "1px solid", borderColor: "divider", "&:hover": { bgcolor: "action.hover" } }}>
                {cat}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* SSR-first service grid — real visible cards for crawlers, hidden after hydration */}
      {!hydrated && effectiveCatalogData.services?.length > 0 && (
        <ServiceGridSSR
          services={effectiveCatalogData.services.map((s: any) => ({
            // Preserve the structured pricing payload. The SSR card and the
            // hydrated card share a formatter, but they can only stay in
            // parity if variants, schedules, fees, and term overrides reach
            // both renderers.
            ...s,
            id: s.id || s.service_id,
            name: s.name || s.service_name,
            brand_name: s.brand_name,
            is_select: s.is_select === true,
            category: s.category,
            description: s.description || s.short_description,
            base_price: s.base_price,
            price_range_min: s.price_range_min,
            price_range_max: s.price_range_max,
            // PR-9 tile rail passthrough.
            tile_unit_basis: s.tile_unit_basis || 'flat',
            tile_price_cents: s.tile_price_cents ?? null,
            tile_unit_label: s.tile_unit_label ?? null,
          }))}
          catalogInstance={catalogInstance}
        />
      )}

      {hydrated && <LayoutRouter
        catalogBranding={{
          name: effectiveCatalogData.catalog.name,
          logo: effectiveCatalogData.catalog.branding?.logo,
          primaryColor: effectiveCatalogData.catalog.branding?.primaryColor,
          website: effectiveCatalogData.catalog.branding?.website,
          headerText: effectiveCatalogData.catalog.branding?.headerText,
        }}
      >
        <Box id="catalog">
          <MarketplaceContainer
            catalogId={catalogSlug}
            catalogInstanceId={effectiveCatalogData.catalog.id}
            initialCatalogData={effectiveCatalogData.catalog}
            initialServices={effectiveCatalogData.services}
            branding={effectiveCatalogData.catalog.branding}
            allowPerks={effectiveCatalogData.catalog.is_main_catalog ? true : (effectiveCatalogData.catalog.allow_perks ?? false)}
          />
        </Box>
      </LayoutRouter>}
    </>
  );
};

export const getServerSideProps: GetServerSideProps<{
  catalogInstance: string;
  catalogData?: CatalogData;
}> = async (context) => {
  const { catalogInstance } = (context.params as { catalogInstance: string }) || {};

  console.log('[SSR-CATALOG] ============================================');
  console.log('[SSR-CATALOG] CatalogInstancePage SSR Start');
  console.log('[SSR-CATALOG] catalogInstance:', catalogInstance);
  console.log('[SSR-CATALOG] ============================================');

  if (!catalogInstance) {
    console.log('[SSR-CATALOG] No catalogInstance, returning 404');
    return { notFound: true };
  }

  // Fetch catalog data server-side for immediate branding availability
  try {
    // Use canonical catalog-context API URL for SSR
    const apiUrl = getInternalApiUrl(`/api/catalog-context/${catalogInstance}`);
    console.log('[SSR-CATALOG] Fetching catalog from:', apiUrl);

    const response = await fetch(apiUrl);
    console.log('[SSR-CATALOG] Catalog API response status:', response.status);

    if (!response.ok) {
      console.log('[SSR-CATALOG] Catalog fetch failed, returning empty props');
      return {
        props: { catalogInstance },
      };
    }

    const catalogResult = await response.json();
    const catalogData = {
      catalog: catalogResult?.catalog || null,
      services: catalogResult?.items || [],
    };
    console.log('[SSR-CATALOG] Catalog data fetched successfully');

    console.log('[SSR-CATALOG] ============================================');
    console.log('[SSR-CATALOG] CatalogInstancePage SSR Complete');
    console.log('[SSR-CATALOG] ============================================');

    return {
      props: { catalogInstance, catalogData },
    };
  } catch (error) {
    console.error('[SSR-CATALOG] Error fetching catalog:', error);
    return {
      props: { catalogInstance },
    };
  }
};

export default CatalogInstancePage;
